// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer from "@flip/App/Components/Cart/CartLineContainer";
import LinesHeader from "@flip/App/Components/Cart/LinesHeader";
import XngCartLineQuantity from "@flip/App/Components/Cart/XngCartLineQuantity";
import LegacyManufacturer from "@flip/App/Components/Product/LegacyManufacturer";
import ProductMultipleSaleQty from "@flip/App/Components/Product/MultipleSaleQty";
import ProductMinimumOrderQty from "@flip/App/Components/Product/ProductMinimumOrderQty";
import cartPageStyles from "@flip/App/SharedStyle/CartPageStyles";
import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers from "@flip/Overrides/Components/ProductPartNumbers";
import { isQuoteOnly, parseBoolean } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

type Props = ReturnType<typeof mapStateToProps>;

export interface RfqQuoteDetailsRequestedDetailsGridStyles {
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    countLabelText?: TypographyPresentationProps;
    countValueText?: TypographyPresentationProps;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    quoteLineInfoGridItem?: GridItemProps;
    quoteLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    quantityAndSubtotalGridItem?: GridItemProps;
    quantityAndSubtotalContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalGridItem?: GridItemProps;
    subtotalText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    footerWrapper?: InjectableCss;
    totalLabelText?: TypographyPresentationProps;
    totalValueText?: TypographyPresentationProps;
    totalWithoutVatText?: TypographyPresentationProps;
    // XNG-Changes
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    productMinimumOrderQty?: TypographyProps;
    productMultipleSaleQty?: TypographyProps;
    qtyLabel?: TypographyProps;
}

export const rfqQuoteDetailsRequestedDetailsGridStyles: RfqQuoteDetailsRequestedDetailsGridStyles = {
    headerWrapper: {
        css: css`
            display: flex;
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    countLabelText: {
        weight: 600,
        css: css`
            margin-left: 5px;
        `,
    },
    countValueText: {
        weight: 600,
    },
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
    },
    quoteLineInfoGridItem: {
        width: 10,
    },
    quoteLineInfoContainer: {
        gap: 20,
    },
    infoLeftColumn: {
        width: [12, 12, 12, 8, 8],
    },
    infoLeftColumnContainer: {
        gap: 10,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    quantityAndSubtotalGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    quantityAndSubtotalContainer: {
        gap: 10,
    },
    quantityGridItem: {
        width: 6,
    },
    quantityHeadingAndText: {
        heading: {
            weight: "bold",
            size: 14,
        },
    },
    subtotalGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    subtotalText: {
        weight: 700,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    footerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: flex-end;
            margin-top: 20px;
        `,
    },
    totalLabelText: {
        size: 20,
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    totalValueText: {
        size: 20,
    },
    totalWithoutVatText: {
        size: 20,
        css: css`
            margin-top: 5px;
        `,
    },
    productImage: {
        ...cartPageStyles.cartLine?.productImage,
    },
    productDescription: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    productPartNumbers: {
        container: {
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    productPrice: {
        ...cartPageStyles.cartLine?.productPrice,
    },
    extendedUnitNetPriceText: {
        ...cartPageStyles.cartLine?.extendedUnitNetPriceText,
    },
    qtyLabel: {
        ...cartPageStyles.cartLine?.extendedUnitNetPriceText?.extendedStyles?.heading,
    },
    productMinimumOrderQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    productMultipleSaleQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
};

const styles = rfqQuoteDetailsRequestedDetailsGridStyles;

const RfqQuoteDetailsRequestedDetailsGrid = ({ quoteState, enableVat, vatPriceDisplay, showPricing }: Props) => {
    const quote = quoteState.value;
    if (!quote || !quote.quoteLineCollection) {
        return null;
    }

    // XNG-Changes Structure
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.headerWrapper}>
                <Typography as="p" {...styles.countValueText}>
                    {quote.quoteLineCollection.length}
                    <Typography {...styles.countLabelText}>
                        {quote.quoteLineCollection.length > 1 ? translate("Products") : translate("Product")}
                    </Typography>
                </Typography>
            </StyledWrapper>
            <LinesHeader />
            {quote.quoteLineCollection.map(quoteLine => (
                <CartLineContainer key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}>
                    <CartLineContainer.Image>
                        <ProductImage product={quoteLine} extendedStyles={styles.productImage} />
                    </CartLineContainer.Image>
                    <CartLineContainer.Description>
                        {/* XNG-Changes: FLIP-507 */}
                        {quoteLine.brand && (
                            <ProductBrand
                                brand={quoteLine.brand}
                                isAuthorized={parseBoolean(quoteLine.properties.isAuthorizedBrand)}
                                extendedStyles={styles.productBrand}
                            />
                        )}
                        <ProductDescription product={quoteLine} extendedStyles={styles.productDescription} />
                        <ProductPartNumbers
                            productNumber={quoteLine.erpNumber}
                            customerProductNumber={quoteLine.customerName}
                            manufacturerItem={quoteLine.manufacturerItem}
                            extendedStyles={styles.productPartNumbers}
                            sku={quoteLine.properties.sku}
                            // XNG-Changes
                            legacyManufacturer={!!quoteLine.properties?.legacyManufacturer}
                        />
                        {/* XNG-Changes */}
                        {quoteLine.properties?.legacyManufacturer && (
                            <LegacyManufacturer
                                legacyManufacturer={quoteLine.properties.legacyManufacturer}
                                legacyManufacturerLink={quoteLine.properties.legacyManufacturerDetailUrl}
                                isAuthorized={parseBoolean(
                                    quoteLine.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                )}
                            />
                        )}
                    </CartLineContainer.Description>
                    <CartLineContainer.MinimumOrderQty>
                        <ProductMinimumOrderQty cartLine={quoteLine} extendedStyles={styles.productMinimumOrderQty} />
                    </CartLineContainer.MinimumOrderQty>
                    <CartLineContainer.MultipleSaleQty>
                        <ProductMultipleSaleQty cartLine={quoteLine} extendedStyles={styles.productMultipleSaleQty} />
                    </CartLineContainer.MultipleSaleQty>
                    <CartLineContainer.Price>
                        <ProductPrice
                            product={quoteLine}
                            currencySymbol={quote.currencySymbol}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={true}
                            showSavingsPercent={true}
                            extendedStyles={styles.productPrice}
                        />
                    </CartLineContainer.Price>
                    <CartLineContainer.Qty>
                        <Typography {...styles.qtyLabel}>{translate("QTY")}</Typography>
                        <XngCartLineQuantity
                            label={translate("QTY")}
                            value={quoteLine.qtyOrdered || 1}
                            editable={false}
                        />
                    </CartLineContainer.Qty>
                    {/* XNG-Changes: FLIP-999 */}
                    <CartLineContainer.Total>
                        {quoteLine.pricing &&
                        showPricing &&
                        !isQuoteOnly(
                            quoteLine.quoteRequired,
                            quoteLine.allowZeroPricing,
                            quoteLine.pricing,
                            undefined,
                            parseBoolean(quoteLine?.properties?.zeroQtyOnHand),
                        ) ? (
                            <>
                                <VisuallyHidden>{translate("Subtotal")}</VisuallyHidden>
                                <SmallHeadingAndText
                                    {...styles.extendedUnitNetPriceText}
                                    data-test-selector="quoteline_extendedUnitNetPrice"
                                    heading={translate("Total")}
                                    text={
                                        enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? quoteLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                            : quoteLine.pricing.extendedUnitNetPriceDisplay
                                    }
                                />
                                {enableVat && (
                                    <>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {vatPriceDisplay === "DisplayWithVat" ||
                                            vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                ? `${translate("Inc. VAT")} (${quoteLine.pricing.vatRate}%)`
                                                : translate("Ex. VAT")}
                                        </Typography>
                                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <Typography {...styles.subtotalWithoutVatText}>
                                                    {quoteLine.pricing.extendedUnitNetPriceDisplay}
                                                </Typography>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {translate("Ex. VAT")}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <VisuallyHidden>{translate("Subtotal")}</VisuallyHidden>
                                <SmallHeadingAndText
                                    {...styles.extendedUnitNetPriceText}
                                    data-test-selector="quoteline_quoteOnly"
                                    heading={translate("Total")}
                                    text="Quote Only"
                                />
                            </>
                        )}
                    </CartLineContainer.Total>
                    <CartLineContainer.LineNotes>
                        {quote.showCostCode && quoteLine.costCode && (
                            <GridItem {...styles.costCodeGridItem}>
                                <Typography {...styles.costCodeValueText} as="p">
                                    <Typography {...styles.costCodeLabelText}>{quote.costCodeLabel}:</Typography>
                                    {quoteLine.costCode}
                                </Typography>
                            </GridItem>
                        )}
                    </CartLineContainer.LineNotes>
                    <CartLineContainer.Remove></CartLineContainer.Remove>
                </CartLineContainer>
            ))}
            {showPricing && (
                <StyledWrapper {...styles.footerWrapper}>
                    <Typography {...styles.totalValueText} as="p">
                        <Typography {...styles.totalLabelText}>{translate("Total")}:</Typography>
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? quote.orderGrandTotalDisplay
                            : quote.orderSubTotalDisplay}
                    </Typography>
                    {enableVat && (
                        <>
                            <Typography as="p" {...styles.vatLabelText}>
                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                    ? translate("Inc. VAT")
                                    : translate("Ex. VAT")}
                            </Typography>
                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <Typography {...styles.totalWithoutVatText}>
                                        {quote.orderSubTotalDisplay}
                                    </Typography>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {translate("Ex. VAT")}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(RfqQuoteDetailsRequestedDetailsGrid);
